import { useTranslation } from 'react-i18next';
import type { ApprovalStepView, WorkflowStepState } from '../types/approvalCenter';

interface ApprovalTimelineProps {
  steps: ApprovalStepView[];
}

function formatDate(iso: string | null, locale: string): string {
  if (!iso) return '-';
  return new Intl.DateTimeFormat(locale, {
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(iso));
}

function stepToneColor(state: WorkflowStepState): string {
  if (state === 'APPROVED' || state === 'COMPLETED') return 'var(--color-success)';
  if (state === 'IN_PROGRESS') return 'var(--color-brand-primary)';
  if (state === 'REJECTED') return 'var(--color-danger)';
  return 'var(--color-text-muted)';
}

function stepIcon(state: WorkflowStepState): string {
  if (state === 'APPROVED' || state === 'COMPLETED') return '✓';
  if (state === 'IN_PROGRESS') return '◀';
  if (state === 'REJECTED') return '✕';
  return '○';
}

export function ApprovalTimeline({ steps }: ApprovalTimelineProps) {
  const { t, i18n } = useTranslation();

  return (
    <div
      role="list"
      aria-label={t('approval.detail.section.timeline')}
      style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
    >
      {steps.map((step, idx) => {
        const color = stepToneColor(step.state);
        const icon = stepIcon(step.state);
        const isCurrent = step.state === 'IN_PROGRESS';
        const isDone = step.state === 'APPROVED' || step.state === 'COMPLETED';
        const isRejected = step.state === 'REJECTED';

        return (
          <div
            key={step.stepName}
            role="listitem"
            aria-current={isCurrent ? 'step' : undefined}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 'var(--spacing-3)',
              position: 'relative',
              paddingBottom: idx < steps.length - 1 ? 'var(--spacing-4)' : 0,
            }}
          >
            {/* 수직 연결선 */}
            {idx < steps.length - 1 && (
              <div
                aria-hidden="true"
                style={{
                  position: 'absolute',
                  left: 15,
                  top: 28,
                  width: 2,
                  bottom: 0,
                  background: isDone ? 'var(--color-success)' : 'var(--color-surface-border)',
                }}
              />
            )}

            {/* 단계 아이콘 */}
            <div
              aria-hidden="true"
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                background: color,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'var(--color-text-inverse)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 700,
                flexShrink: 0,
                border: isCurrent ? `3px solid var(--color-brand-primary)` : 'none',
                outline: isCurrent ? `2px solid var(--color-surface-background)` : 'none',
                boxShadow: isCurrent ? `0 0 0 1px var(--color-brand-primary)` : 'none',
              }}
            >
              {icon}
            </div>

            {/* 단계 정보 */}
            <div style={{ flex: 1, paddingTop: 'var(--spacing-1)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)', flexWrap: 'wrap' }}>
                <span
                  style={{
                    fontWeight: isCurrent ? 700 : 500,
                    fontSize: 'var(--font-size-sm)',
                    color: isCurrent ? 'var(--color-text-primary)' : 'var(--color-text-secondary)',
                  }}
                >
                  {step.stepLabel || t(`approval.step.${step.stepName}`, { defaultValue: step.stepName })}
                </span>
                <span
                  style={{
                    fontSize: 'var(--font-size-xs)',
                    color,
                    fontWeight: 600,
                    background: `color-mix(in srgb, ${color} 12%, transparent)`,
                    padding: '1px var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                  }}
                >
                  {t(`approval.stepState.${step.state}`, { defaultValue: step.state })}
                </span>
                {isCurrent && (
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-brand-primary)',
                      fontWeight: 600,
                    }}
                  >
                    {t('approval.detail.timeline.current')}
                  </span>
                )}
              </div>

              <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
                <span>{step.approverRole}</span>
                {(isDone || isRejected) && step.decidedBy && (
                  <span> · {step.decidedBy}</span>
                )}
                {(isDone || isRejected) && step.decidedAt && (
                  <span> · {formatDate(step.decidedAt, i18n.language)}</span>
                )}
              </div>

              {(isDone || isRejected) && step.comment && (
                <div
                  style={{
                    marginTop: 'var(--spacing-1)',
                    fontSize: 'var(--font-size-xs)',
                    color: 'var(--color-text-secondary)',
                    fontStyle: 'italic',
                    background: 'var(--color-surface-muted)',
                    padding: 'var(--spacing-1) var(--spacing-2)',
                    borderRadius: 'var(--radius-sm)',
                    borderLeft: `3px solid ${color}`,
                  }}
                >
                  "{step.comment}"
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/** 결재 상신 단계를 포함한 전체 타임라인 (상신 단계 + 결재선 단계) */
export function ApprovalFullTimeline({
  steps,
  requesterName,
  submittedAt,
}: {
  steps: ApprovalStepView[];
  requesterName: string | null;
  submittedAt: string;
}) {
  const { t, i18n } = useTranslation();

  return (
    <div
      role="list"
      aria-label={t('approval.detail.section.timeline')}
      style={{ display: 'flex', flexDirection: 'column', gap: 0 }}
    >
      {/* 상신 단계 (고정) */}
      <div
        role="listitem"
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 'var(--spacing-3)',
          position: 'relative',
          paddingBottom: 'var(--spacing-4)',
        }}
      >
        <div
          aria-hidden="true"
          style={{
            position: 'absolute',
            left: 15,
            top: 28,
            width: 2,
            bottom: 0,
            background: 'var(--color-success)',
          }}
        />
        <div
          aria-hidden="true"
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--color-success)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'var(--color-text-inverse)',
            fontSize: 'var(--font-size-sm)',
            fontWeight: 700,
            flexShrink: 0,
          }}
        >
          ✓
        </div>
        <div style={{ flex: 1, paddingTop: 'var(--spacing-1)' }}>
          <div style={{ fontWeight: 500, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-secondary)' }}>
            상신
          </div>
          <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--spacing-1)' }}>
            {requesterName ?? '-'} · {formatDate(submittedAt, i18n.language)}
          </div>
        </div>
      </div>

      {/* 결재선 단계들 */}
      <ApprovalTimeline steps={steps} />
    </div>
  );
}
